import type { AIModelChoices } from 'api';
import { useTranslation } from 'i18n';
import { logger } from 'monitor';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Container, useTheme } from 'ui';

import { isOtherAiModel, usePresentationPreference } from '../../../evidence';
import { HelpGuideDialog } from '../../../help-guides';
import { useAlertMessagePriority } from '../../hooks/useAlertMessagePriority/useAlertMessagePriority';
import { useCamera } from '../../hooks/useCamera/useCamera';
import { relativeZIndex } from '../../index';
import type { PreviewGalleryImage } from '../../types';
import { captureImage, getAspectRatio } from '../../utils/captureImage/captureImage';
import { CameraFooter } from '../CameraFooter/CameraFooter';
import { CameraHeader } from '../CameraHeader/CameraHeader';
import { CountdownOverlay } from '../CountdownOverlay/CountdownOverlay';
import { InfoText } from '../InfoText/InfoText';
import { PreviewImageReview } from '../PreviewImageReview/PreviewImageReview';
import { TiltOverlay } from '../TiltOverlay/TiltOverlay';
import { Video } from '../Video/Video';
import { VideoContainer } from '../VideoContainer/VideoContainer';

export interface CameraProps {
  handleCapture: (photo: File) => void;
  handleClose: () => void;
  handleOnComplete?: () => void;
  previewImages: PreviewGalleryImage[];
  handleDeletePreviewImage?: (image: PreviewGalleryImage) => void;
  showDeleteInPreview?: boolean;
  showAerosols?: boolean;
  aiModel: AIModelChoices;
  showAnnotations?: boolean;
  showFilters?: boolean;
}

export const Camera = ({
  handleCapture,
  handleClose,
  handleOnComplete,
  previewImages,
  handleDeletePreviewImage,
  showDeleteInPreview = true,
  showAerosols = false,
  aiModel,
  showAnnotations = true,
  showFilters = true,
}: CameraProps) => {
  const { t } = useTranslation('features');
  const [selectedImage, setSelectedImage] = useState<PreviewGalleryImage | null>(null);
  const [actionDialogIsOpen, setActionDialogIsOpen] = useState(false);
  const [guideIsOpen, setGuideIsOpen] = useState(false);
  const [showCountdownOverlay, setShowCountdownOverlay] = useState(false);
  const captureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCapturingRef = useRef<boolean>(false);

  const { obfuscateAi } = usePresentationPreference();

  const {
    releaseWakeLock,
    autoCapturing,
    toggleAutoCapture,
    stream,
    countdown,
    shouldCapture,
    markCaptureTriggered,
    isLoading: cameraStreamIsLoading,
    isPortrait,
    isDeviceReady,
    releaseStream,
    skipGuidance,
    tiltDirection,
    flashEnabled,
    toggleFlash,
  } = useCamera({ aiModel });

  const { alertState, flashMessage } = useAlertMessagePriority({ isDeviceReady, skipGuidance, tiltDirection });

  const theme = useTheme();

  const aspectRatio = getAspectRatio({ aiModel, isPortrait });

  const capture = useCallback(async () => {
    if (isCapturingRef.current) {
      logger.info('Camera capture ignored because a capture is already in progress');
      return;
    }

    const video = document.querySelector('video') as HTMLVideoElement;
    if (!video) {
      logger.warn('Camera capture ignored because no video element is available');
      return;
    }

    logger.info('Camera capture requested', {
      aiModel,
      previewImageCount: previewImages.length,
    });

    isCapturingRef.current = true;

    const capturedImage = await captureImage({ video });

    if (!capturedImage) {
      logger.warn('Camera capture did not produce an image');
      isCapturingRef.current = false;
      return;
    }

    setShowCountdownOverlay(true);
    handleCapture(capturedImage);
    setTimeout(() => {
      setShowCountdownOverlay(false);
      isCapturingRef.current = false;
    }, 1000);
  }, [aiModel, handleCapture, previewImages.length]);

  const captureDisabled = cameraStreamIsLoading || !isDeviceReady;
  const completeDisabled = previewImages.length === 0;

  useEffect(() => {
    if (shouldCapture && !isCapturingRef.current) {
      markCaptureTriggered();

      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
        captureTimeoutRef.current = null;
      }

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowCountdownOverlay(true);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      capture();
      captureTimeoutRef.current = setTimeout(() => {
        setShowCountdownOverlay(false);
        captureTimeoutRef.current = null;
      }, 1000);
    }
  }, [shouldCapture, capture, markCaptureTriggered]);

  const handleToggleAutoCapture = () => {
    logger.info('Camera auto-capture toggled', {
      aiModel,
      nextAutoCapturing: !autoCapturing,
    });
    toggleAutoCapture();
    flashMessage({
      message: autoCapturing
        ? t('cameraGuidance.camera.notifications.autoCaptureOff')
        : t('cameraGuidance.camera.notifications.autoCaptureOn'),
      severity: 'info',
    });
  };

  const handlePreviewImageClick = (image: PreviewGalleryImage) => {
    logger.info('Previewing image', {
      image,
    });
    setSelectedImage(image);
    setActionDialogIsOpen(true);
  };

  const handleActionDialogClose = () => {
    logger.info('Camera preview dialog closed');
    setActionDialogIsOpen(false);
  };

  const deleteImage = () => {
    if (!selectedImage) {
      logger.error('No image selected for deletion');
      return;
    }
    logger.info('Deleting preview image from camera review', {
      imageId: selectedImage.id,
      imageStatus: selectedImage.status,
    });
    handleDeletePreviewImage?.(selectedImage);
    handleActionDialogClose();
  };

  const handleOpenGuide = () => {
    logger.info('Camera help guide opened', { aiModel });
    setGuideIsOpen(true);
  };

  const handleCloseGuide = () => {
    logger.info('Camera help guide closed', { aiModel });
    setGuideIsOpen(false);
  };

  useEffect(() => {
    return () => {
      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
      }
    };
  }, []);

  const handleStopCamera = () => {
    logger.info('Camera stop requested', {
      aiModel,
      previewImageCount: previewImages.length,
    });
    releaseWakeLock();
    handleClose();
    releaseStream();
  };

  const handleCompleteCamera = () => {
    logger.info('Camera completion requested', {
      aiModel,
      previewImageCount: previewImages.length,
    });
    releaseWakeLock();
    handleOnComplete?.();
    releaseStream();
  };

  const sopRejectedImages = previewImages.filter((image) => image.status === 'warning');
  const rejectedPhotosTitlePrefix = obfuscateAi
    ? t('cameraGuidance.camera.rejectedPhotos.titlePrefixObfuscated')
    : t('cameraGuidance.camera.rejectedPhotos.titlePrefixWithAi');

  return (
    <>
      <Container
        translate="no"
        sx={{
          '& *': {
            translate: 'no',
          },
          '@supports (height: 100dvh)': {
            height: '100dvh',
          },
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          '@media all and (display-mode: standalone)': {
            height: '100lvh',
          },
          backgroundColor: theme.palette.secondary.main,
          display: 'flex',
          flexDirection: isPortrait ? 'column' : 'row',
          height: '100vh',
          margin: 0,
          padding: '0 !important',
          position: 'fixed',
          width: '100dvw',
        }}
        data-testid="camera"
      >
        <CameraHeader
          autoCapturing={autoCapturing}
          toggleAutoCapture={handleToggleAutoCapture}
          handleStopCamera={handleStopCamera}
          isPortrait={isPortrait}
          handleCompleteCameraSession={handleCompleteCamera}
          completeDisabled={completeDisabled}
          handleFlashToggle={toggleFlash}
          flashEnabled={flashEnabled}
          obfuscateAi={obfuscateAi}
        />
        <VideoContainer isPortrait={isPortrait} aspectRatio={aspectRatio} zIndex={relativeZIndex.infoText}>
          <InfoText
            alertState={
              sopRejectedImages.length > 0
                ? {
                    message: t('cameraGuidance.camera.rejectedPhotos.message'),
                    severity: 'error',
                    showAlert: true,
                    tiltDirection: undefined,
                    title: t('cameraGuidance.camera.rejectedPhotos.title', {
                      values: {
                        count: sopRejectedImages.length,
                        countLabel:
                          sopRejectedImages.length === 1
                            ? t('cameraGuidance.camera.rejectedPhotos.photo')
                            : t('cameraGuidance.camera.rejectedPhotos.photos'),
                        prefix: rejectedPhotosTitlePrefix,
                      },
                    }),
                  }
                : alertState
            }
          />
        </VideoContainer>
        {showCountdownOverlay || (autoCapturing && countdown > 0) ? (
          <VideoContainer isPortrait={isPortrait} aspectRatio={aspectRatio} zIndex={relativeZIndex.countdownOverlay}>
            <CountdownOverlay countdown={showCountdownOverlay ? 0 : countdown} />
          </VideoContainer>
        ) : null}

        <VideoContainer isPortrait={isPortrait} aspectRatio={aspectRatio} zIndex={relativeZIndex.tiltIndicator}>
          <TiltOverlay isCameraLevel={isDeviceReady} />
        </VideoContainer>

        <VideoContainer isPortrait={isPortrait} aspectRatio={aspectRatio} zIndex={relativeZIndex.videoContainer}>
          <Video stream={stream} isLoading={cameraStreamIsLoading} />
        </VideoContainer>

        <CameraFooter
          capture={capture}
          captureDisabled={captureDisabled}
          completeDisabled={completeDisabled}
          slotProps={{
            previewGallery: {
              images: previewImages,
              isPortrait,
              onClick: handlePreviewImageClick,
            },
          }}
          handleOpenGuide={handleOpenGuide}
          handleOnComplete={handleCompleteCamera}
          isPortrait={isPortrait}
        />
      </Container>

      {selectedImage ? (
        <PreviewImageReview
          open={actionDialogIsOpen}
          onClose={handleActionDialogClose}
          selectedImage={selectedImage}
          onDeleteImage={deleteImage}
          showDeleteButton={showDeleteInPreview}
          isPortrait={isPortrait}
          showAerosols={showAerosols}
          showAnnotations={showAnnotations}
          showFilters={showFilters}
        />
      ) : null}

      {!isOtherAiModel(aiModel) && (
        <HelpGuideDialog
          open={guideIsOpen}
          onClose={handleCloseGuide}
          aiModel={aiModel}
          onComplete={handleCloseGuide}
        />
      )}
    </>
  );
};
